/**
 * ECOSMITH Product Story Component
 * Scroll-driven 3D CIP Vessel assembly using Three.js + GSAP ScrollTrigger
 * 6 scenes: Disassembly → Assembly → 3 Features → Complete Rotation
 * Updated: Rotating gradient mesh background (brand green), glossy steel materials
 */

import * as THREE from 'three';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

class EcoProductStory extends HTMLElement {
  constructor() {
    super();
    this.renderer = null;
    this.scene = null;
    this.camera = null;
    this.parts = {};
    this.annotations = [];
    this.animFrame = null;
    this.scrollTrigger = null;
    this.clock = new THREE.Clock();
    this.progress = 0;
    this.finalRotation = false;
  }

  connectedCallback() {
    this.innerHTML = this._template();
    this._initThree();
    this._buildBackground();
    this._buildVessel();
    this._buildAnnotations();
    this._initScrollTrigger();
    this._animate();
  }

  disconnectedCallback() {
    if (this.animFrame) cancelAnimationFrame(this.animFrame);
    if (this.scrollTrigger) this.scrollTrigger.kill();
    if (this.renderer) this.renderer.dispose();
  }

  _template() {
    return `
      <style>
        eco-product-story {
          display: block;
          position: relative;
          width: 100%;
        }

        .ps-scroll-space {
          height: 500vh;
        }

        .ps-sticky {
          position: sticky;
          top: 0;
          width: 100%;
          height: 100vh;
          overflow: hidden;
          background: #0a0e14;
        }

        .ps-canvas {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
        }

        /* Rotating gradient mesh overlay — sits between canvas and UI */
        .ps-gradient-bg {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
          /* Two radial spots + base tone, sized 200%x200% so rotation never exposes edges */
          width: 200%;
          height: 200%;
          top: -50%;
          left: -50%;
          background:
            radial-gradient(ellipse 55% 45% at 30% 35%, rgba(80, 170, 100, 0.18) 0%, transparent 60%),
            radial-gradient(ellipse 50% 60% at 70% 65%, rgba(50, 120, 70, 0.13) 0%, transparent 55%),
            radial-gradient(ellipse 70% 70% at 50% 50%, rgba(20, 80, 40, 0.22) 0%, transparent 70%);
          animation: ps-grad-rotate 28s ease-in-out infinite;
        }

        @keyframes ps-grad-rotate {
          0%,  100% { transform: translate(0%,   0%)   rotate(0deg);   }
          33%        { transform: translate(-3%,  -3%)  rotate(120deg); }
          66%        { transform: translate( 3%,   3%)  rotate(240deg); }
        }

        /* Section label top */
        .ps-section-label {
          position: absolute;
          top: 80px;
          left: 50%;
          transform: translateX(-50%);
          text-align: center;
          z-index: 10;
          pointer-events: none;
        }

        .ps-eyebrow {
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          color: #94B58A;
          margin-bottom: 8px;
        }

        .ps-title {
          font-size: clamp(1.6rem, 3vw, 2.4rem);
          font-weight: 800;
          color: #fff;
          letter-spacing: -0.02em;
          line-height: 1.1;
          white-space: nowrap;
          opacity: 0;
          transform: translateY(12px);
          transition: opacity 0.5s ease, transform 0.5s ease;
        }

        .ps-title.visible {
          opacity: 1;
          transform: none;
        }

        /* Annotation chips */
        .ps-annotation {
          position: absolute;
          z-index: 10;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .ps-annotation.visible {
          opacity: 1;
        }

        .ps-annotation-chip {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 16px;
          background: rgba(4, 18, 8, 0.88);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(148, 181, 138, 0.4);
          border-radius: 12px;
          white-space: nowrap;
        }

        .ps-annotation-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #94B58A;
          flex-shrink: 0;
          box-shadow: 0 0 0 3px rgba(153, 167, 149, 0.54);
          animation: ps-pulse 2s infinite;
        }

        @keyframes ps-pulse {
          0%, 100% { box-shadow: 0 0 0 3px rgba(176, 219, 163, 0.78); }
          50%       { box-shadow: 0 0 0 6px rgba(148, 181, 138, 0.52); }
        }

        .ps-annotation-line {
          font-size: 0.75rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: #94B58A;
        }

        .ps-annotation-desc {
          font-size: 0.8rem;
          color: rgba(255,255,255,0.65);
          margin-top: 2px;
        }

        /* Progress dots */
        .ps-progress {
          position: absolute;
          right: 32px;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          gap: 10px;
          z-index: 10;
        }

        .ps-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: rgba(255,255,255,0.25);
          transition: background 0.3s ease, transform 0.3s ease;
        }

        .ps-dot.active {
          background: #94B58A;
          transform: scale(1.5);
        }

        /* Scroll cue */
        .ps-scroll-cue {
          position: absolute;
          bottom: 32px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          z-index: 10;
          opacity: 1;
          transition: opacity 0.5s ease;
        }

        .ps-scroll-cue.hidden { opacity: 0; }

        .ps-scroll-cue span {
          font-size: 0.65rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.4);
        }

        .ps-scroll-line {
          width: 1px;
          height: 36px;
          background: linear-gradient(to bottom, rgba(148,181,138,0.7), transparent);
          animation: ps-line-anim 1.6s ease-in-out infinite;
        }

        @keyframes ps-line-anim {
          0%   { transform: scaleY(0); transform-origin: top; }
          50%  { transform: scaleY(1); transform-origin: top; }
          51%  { transform: scaleY(1); transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }

        /* Vignette: darkens edges slightly, adds depth over gradient */
        .ps-vignette {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(5, 8, 14, 0.62) 100%);
          z-index: 2;
        }
      </style>

      <div class="ps-scroll-space">
        <div class="ps-sticky">
          <canvas class="ps-canvas" id="ps-canvas"></canvas>

          <!-- Rotating brand-green gradient mesh -->
          <div class="ps-gradient-bg"></div>

          <!-- Vignette depth layer -->
          <div class="ps-vignette"></div>

          <!-- Section label -->
          <div class="ps-section-label">
            <div class="ps-eyebrow">Cleaning System Series</div>
            <div class="ps-title" id="ps-title">Components</div>
          </div>

          <!-- Annotations: injected by JS -->
          <div id="ps-annotations"></div>

          <!-- Progress dots -->
          <div class="ps-progress" id="ps-progress"></div>

          <!-- Scroll cue -->
          <div class="ps-scroll-cue" id="ps-scroll-cue">
            <span>Scroll to explore</span>
            <div class="ps-scroll-line"></div>
          </div>
        </div>
      </div>
    `;
  }

  // ───────────────────────── THREE SETUP ─────────────────────────

  _initThree() {
    const canvas = this.querySelector('#ps-canvas');
    const W = this.offsetWidth || window.innerWidth;
    const H = window.innerHeight;

    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(W, H);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    // Increased from 1.0 → 1.6 so the steel reads bright and reflective
    this.renderer.toneMappingExposure = 1.6;

    this.scene = new THREE.Scene();
    // Fog: cool mid-grey so parts read as bright steel
    this.scene.fog = new THREE.FogExp2(0x141820, 0.028);
    // Transparent — the CSS gradient bg shows through
    this.scene.background = null;

    this.camera = new THREE.PerspectiveCamera(45, W / H, 0.1, 200);
    this.camera.position.set(0, 1.5, 12);
    this.camera.lookAt(0, 0, 0);

    // ── Lighting ──────────────────────────────────────────────────

    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileEquirectangularShader();
    const envScene = new THREE.Scene();
    envScene.add(new THREE.DirectionalLight(0xffffff, 2.0));
    envScene.add(new THREE.AmbientLight(0xffffff, 0.5));
    this.scene.environment = pmremGenerator.fromScene(envScene).texture;

    // Ambient — brighter base so shadowed faces read grey not black
    this.scene.add(new THREE.AmbientLight(0xffffff, 0.65));

    // Key — warm, bright, soft shadows
    const keyLight = new THREE.SpotLight(0xfff5e6, 5.0);
    keyLight.position.set(6, 8, 8);
    keyLight.angle = Math.PI / 4;
    keyLight.penumbra = 0.5;
    keyLight.decay = 1.5;
    keyLight.distance = 40;
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    keyLight.shadow.camera.near = 1;
    keyLight.shadow.camera.far = 30;
    keyLight.shadow.bias = -0.0005;
    this.scene.add(keyLight);

    // Fill — brighter cool silver, fills shadow side to keep it grey
    const fillLight = new THREE.DirectionalLight(0xdde8ff, 2.2);
    fillLight.position.set(-8, 3, 2);
    this.scene.add(fillLight);

    // Rim — separates from bg
    const rimLight = new THREE.SpotLight(0xffffff, 4.5);
    rimLight.position.set(-2, 5, -10);
    rimLight.angle = Math.PI / 3;
    rimLight.penumbra = 0.8;
    rimLight.decay = 2.0;
    rimLight.distance = 30;
    this.scene.add(rimLight);

    // Accent — Ecosmith green from below, warm bounce
    const accentLight = new THREE.PointLight(0x94B58A, 3.0, 18);
    accentLight.position.set(0, -4, 2);
    this.scene.add(accentLight);

    // Second green accent — top rear, adds green sheen to top surfaces
    const topGreen = new THREE.PointLight(0x5cb87a, 2.0, 20);
    topGreen.position.set(0, 8, -3);
    this.scene.add(topGreen);

    this.lights = { key: keyLight, fill: fillLight, rim: rimLight, accent: accentLight };

    // Grid — cool steel grey to match new look
    const grid = new THREE.GridHelper(40, 40, 0x2a3040, 0x1a2030);
    grid.position.y = -3.5;
    grid.material.transparent = true;
    grid.material.opacity = 0.4;
    this.scene.add(grid);
    this.grid = grid;

    window.addEventListener('resize', () => this._onResize(), { passive: true });
  }

  _onResize() {
    const W = this.offsetWidth || window.innerWidth;
    const H = window.innerHeight;
    this.camera.aspect = W / H;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(W, H);
    ScrollTrigger.refresh();
  }

  // ───────────────────────── GRADIENT MESH BACKGROUND (Three.js layer) ─────────────────────────
  // A large vertex-colored plane placed behind the vessel adds a subtle
  // 3D-parallax shift as the camera moves; the CSS layer handles the rotation.

  _buildBackground() {
    const geo = new THREE.PlaneGeometry(80, 60, 14, 14);
    const colors = [];
    const pos = geo.attributes.position;

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      // Radial distance 0 (centre) → 1 (edge)
      const dist = Math.sqrt((x / 40) ** 2 + (y / 30) ** 2);
      const t = Math.max(0, 1 - dist * 1.3);
      // Centre: bright steel-silver / Edge: cool dark charcoal
      colors.push(
        0.06 + t * 0.30,   // R
        0.07 + t * 0.32,   // G
        0.10 + t * 0.36    // B
      );
    }

    geo.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

    const mat = new THREE.MeshBasicMaterial({
      vertexColors: true,
      side: THREE.FrontSide,
      transparent: true,
      opacity: 0.85,
    });

    const bgPlane = new THREE.Mesh(geo, mat);
    bgPlane.position.set(0, 0, -14);
    this.scene.add(bgPlane);
    this.bgPlane = bgPlane;

    // Additive glow — canvas-based radial gradient texture
    const glowCanvas = document.createElement('canvas');
    glowCanvas.width = 512;
    glowCanvas.height = 512;
    const ctx = glowCanvas.getContext('2d');
    const grad = ctx.createRadialGradient(256, 256, 0, 256, 256, 256);
    grad.addColorStop(0, 'rgba(180, 200, 230, 0.20)');
    grad.addColorStop(0.35, 'rgba(100, 130, 180, 0.12)');
    grad.addColorStop(0.70, 'rgba( 50,  70, 110, 0.06)');
    grad.addColorStop(1, 'rgba(  0,   0,   0, 0.00)');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 512);

    const glowTex = new THREE.CanvasTexture(glowCanvas);
    const glowMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(55, 42),
      new THREE.MeshBasicMaterial({
        map: glowTex,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    glowMesh.position.set(0, 0, -13);
    this.scene.add(glowMesh);
    this.bgGlow = glowMesh;
  }

  // ───────────────────────── BUILD VESSEL ─────────────────────────

  // Glossy stainless steel: near-mirror finish, cool silver tone
  _mat(color = 0xe2e6ec, roughness = 0.05, metalness = 0.98) {
    return new THREE.MeshStandardMaterial({
      color,
      roughness,
      metalness,
      envMapIntensity: 3.2,
    });
  }

  _buildVessel() {
    const group = new THREE.Group();
    this.scene.add(group);
    this.vesselGroup = group;

    // ── Frame ──
    const frameGroup = new THREE.Group();
    // Slightly warm-grey steel for the frame uprights
    const fMat = this._mat(0xc8cdd5, 0.10, 0.96);
    const fGeo = new THREE.BoxGeometry(0.15, 6, 0.15);
    const fLeft = new THREE.Mesh(fGeo, fMat);
    fLeft.position.set(-1.1, 0, -0.6);
    const fRight = new THREE.Mesh(fGeo, fMat);
    fRight.position.set(1.1, 0, -0.6);

    for (let i = -2; i <= 2; i += 2) {
      const fCross = new THREE.Mesh(new THREE.BoxGeometry(2.35, 0.15, 0.15), fMat);
      fCross.position.set(0, i, -0.6);
      frameGroup.add(fCross);
    }
    frameGroup.add(fLeft, fRight);
    frameGroup.castShadow = true;
    group.add(frameGroup);
    this.parts.frame = frameGroup;

    // ── Cabinet — near-mirror stainless panels ──
    const cabGroup = new THREE.Group();
    // Bright near-mirror stainless panels — glossy silver
    const cabMat = this._mat(0xd0d5de, 0.03, 1.0);
    const topCab = new THREE.Mesh(new THREE.BoxGeometry(2.1, 1.8, 1.2), cabMat);
    topCab.position.set(0, 0.5, 0);
    cabGroup.add(topCab);

    const botCab = new THREE.Mesh(new THREE.BoxGeometry(2.1, 2.2, 1.2), cabMat);
    botCab.position.set(0, -1.6, 0);
    cabGroup.add(botCab);
    cabGroup.castShadow = true;
    group.add(cabGroup);
    this.parts.cabinet = cabGroup;

    // ── Hose Reel ──
    const reelGroup = new THREE.Group();
    // Polished disc cheeks — bright chrome-like
    const reelMat = this._mat(0xc8cdd5, 0.06, 0.97);
    const cheekGeo = new THREE.CylinderGeometry(1.2, 1.2, 0.05, 48);

    const cheek1 = new THREE.Mesh(cheekGeo, reelMat);
    cheek1.rotation.z = Math.PI / 2;
    cheek1.position.x = -0.4;
    const cheek2 = new THREE.Mesh(cheekGeo, reelMat);
    cheek2.rotation.z = Math.PI / 2;
    cheek2.position.x = 0.4;
    reelGroup.add(cheek1, cheek2);

    // Centre drum — slightly matte
    const drum = new THREE.Mesh(
      new THREE.CylinderGeometry(0.5, 0.5, 0.75, 32),
      this._mat(0xb0b5be, 0.14, 0.92)
    );
    drum.rotation.z = Math.PI / 2;
    reelGroup.add(drum);

    // Food-grade blue hose coil
    const hoseMat = new THREE.MeshStandardMaterial({ color: 0x1a73e8, roughness: 0.55, metalness: 0.08 });
    const hoseCoil = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 0.9, 0.76, 40), hoseMat);
    hoseCoil.rotation.z = Math.PI / 2;
    reelGroup.add(hoseCoil);

    reelGroup.position.set(0, 3.2, 0);
    group.add(reelGroup);
    this.parts.reel = reelGroup;

    // ── Lances ──
    const lancesGroup = new THREE.Group();
    // Bright polished lances — near-mirror
    const lMat = this._mat(0xe0e4ea, 0.03, 1.0);

    const l1 = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 2.5, 16), lMat);
    l1.position.set(1.2, -1.0, 0.2);
    const handle1 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.06, 0.4, 16),
      new THREE.MeshStandardMaterial({ color: 0x118833, roughness: 0.65 })
    );
    handle1.position.set(1.2, -0.2, 0.2);
    lancesGroup.add(l1, handle1);

    const l2 = new THREE.Mesh(new THREE.CylinderGeometry(0.04, 0.04, 2.5, 16), lMat);
    l2.position.set(1.2, -1.0, 0.4);
    const handle2 = new THREE.Mesh(
      new THREE.CylinderGeometry(0.06, 0.06, 0.4, 16),
      hoseMat
    );
    handle2.position.set(1.2, -0.2, 0.4);
    lancesGroup.add(l2, handle2);

    group.add(lancesGroup);
    this.parts.lances = lancesGroup;

    // ── Spray Gun ──
    const gunGroup = new THREE.Group();
    const gunBody = new THREE.Mesh(new THREE.BoxGeometry(0.3, 0.1, 0.6), hoseMat);
    const gunHandle = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.4, 0.15), hoseMat);
    gunHandle.position.set(0, -0.2, 0.2);
    gunHandle.rotation.x = -Math.PI / 6;
    gunGroup.add(gunBody, gunHandle);
    gunGroup.position.set(-1.8, 2.5, 0.5);
    gunGroup.rotation.y = -Math.PI / 4;
    group.add(gunGroup);
    this.parts.gun = gunGroup;

    this._storeAssembledPositions();
    this._scatterParts();
  }

  _storeAssembledPositions() {
    this._assembled = {};
    ['frame', 'cabinet', 'reel', 'lances', 'gun'].forEach(k => {
      const p = this.parts[k];
      this._assembled[k] = {
        x: p.position.x, y: p.position.y, z: p.position.z,
        rx: p.rotation.x, ry: p.rotation.y, rz: p.rotation.z
      };
    });
  }

  _scatterParts() {
    const offsets = {
      frame: { x: 0, y: 0, z: -5 },
      cabinet: { x: 0, y: 0, z: 5 },
      reel: { x: -6, y: 5, z: 0 },
      lances: { x: 5, y: -4, z: 2 },
      gun: { x: -4, y: -2, z: 3 }
    };
    Object.entries(offsets).forEach(([k, off]) => {
      const p = this.parts[k];
      const a = this._assembled[k];
      p.position.set(a.x + off.x, a.y + off.y, a.z + off.z);
      p.rotation.set(off.x * 0.3, off.y * 0.2, off.z * 0.25);
    });
  }

  // ───────────────────────── ANNOTATIONS ─────────────────────────

  _buildAnnotations() {
    const container = this.querySelector('#ps-annotations');

    const data = [
      { id: 'cabinet', label: 'Stainless Steel Cabinet', desc: 'Hygienic enclosure for high-pressure systems', x: '65%', y: '42%' },
      { id: 'reel', label: 'Auto-Retracting Reel', desc: 'Spring-driven spool with food-grade blue hose', x: '55%', y: '22%' },
      { id: 'lances', label: 'Interchangeable Lances', desc: 'Quick-connect nozzles for varied spray patterns', x: '60%', y: '72%' },
    ];

    data.forEach(d => {
      const el = document.createElement('div');
      el.className = 'ps-annotation';
      el.id = `ann-${d.id}`;
      el.style.left = d.x;
      el.style.top = d.y;
      el.innerHTML = `
        <div class="ps-annotation-chip">
          <div class="ps-annotation-dot"></div>
          <div>
            <div class="ps-annotation-line">${d.label}</div>
            <div class="ps-annotation-desc">${d.desc}</div>
          </div>
        </div>
      `;
      container.appendChild(el);
      this.annotations.push({ id: d.id, el });
    });

    // Progress dots (6 scenes)
    const prog = this.querySelector('#ps-progress');
    this._dots = [];
    const labels = ['Components', 'Assembly', 'Cabinet', 'Hose Reel', 'Lances', 'Complete'];
    for (let i = 0; i < 6; i++) {
      const dot = document.createElement('div');
      dot.className = 'ps-dot';
      dot.title = labels[i];
      prog.appendChild(dot);
      this._dots.push(dot);
    }
    this._sceneLabels = labels;
  }

  // ───────────────────────── SCROLL TRIGGER ─────────────────────────

  _initScrollTrigger() {
    const sticky = this.querySelector('.ps-sticky');
    const cue = this.querySelector('#ps-scroll-cue');

    const tl = gsap.timeline({ paused: true });

    // Scene 0→1: Assemble
    ['frame', 'cabinet', 'reel', 'lances', 'gun'].forEach(k => {
      const part = this.parts[k];
      const a = this._assembled[k];
      tl.to(part.position, { x: a.x, y: a.y, z: a.z, duration: 2, ease: 'power3.out' }, 1);
      tl.to(part.rotation, { x: a.rx, y: a.ry, z: a.rz, duration: 2, ease: 'power2.out' }, 1);
    });

    // Camera movements
    tl.to(this.camera.position, { x: 0, y: 1.5, z: 12, duration: 2 }, 1);
    tl.to(this.camera.position, { x: 2.5, y: -0.5, z: 6, duration: 2, ease: 'power2.inOut' }, 3);
    tl.to(this.camera.position, { x: 0.5, y: 4.5, z: 6, duration: 2, ease: 'power2.inOut' }, 5);
    tl.to(this.camera.position, { x: 2.5, y: -1.0, z: 4.5, duration: 2, ease: 'power2.inOut' }, 7);
    tl.to(this.camera.position, { x: 0, y: 1.5, z: 13, duration: 2, ease: 'power2.inOut' }, 9);

    this._tl = tl;

    this.scrollTrigger = ScrollTrigger.create({
      trigger: this,
      start: 'top top',
      end: 'bottom bottom',
      scrub: 1.5,
      pin: sticky,
      pinSpacing: false,
      onUpdate: (self) => {
        this.progress = self.progress;
        tl.progress(self.progress);
        this._updateCameraLookAt(self.progress);

        const scene = Math.min(Math.floor(self.progress * 6), 5);
        this._updateScene(scene, self.progress);

        if (self.progress > 0.05) cue.classList.add('hidden');
        else cue.classList.remove('hidden');
      }
    });
  }

  _updateCameraLookAt(p) {
    let target;
    if (p < 0.33) target = new THREE.Vector3(0, 0, 0);
    else if (p < 0.50) target = new THREE.Vector3(0, -0.5, 0);
    else if (p < 0.67) target = new THREE.Vector3(0, 3.2, 0);
    else if (p < 0.83) target = new THREE.Vector3(1.2, -1.0, 0);
    else target = new THREE.Vector3(0, 1.0, 0);

    if (!this._lookAtTarget) this._lookAtTarget = new THREE.Vector3(0, 0, 0);
    this._lookAtTarget.lerp(target, 0.08);
    this.camera.lookAt(this._lookAtTarget);
  }

  _updateScene(scene, progress) {
    const titleEl = this.querySelector('#ps-title');
    const newLabel = this._sceneLabels[scene] || this._sceneLabels[5];

    if (titleEl.textContent !== newLabel) {
      titleEl.classList.remove('visible');
      setTimeout(() => {
        titleEl.textContent = newLabel;
        titleEl.classList.add('visible');
      }, 220);
    }

    const annMap = { 2: 'cabinet', 3: 'reel', 4: 'lances' };
    this.annotations.forEach(({ id, el }) => {
      const showInScene = Object.entries(annMap).find(([s, aid]) => aid === id)?.[0];
      el.classList.toggle('visible', String(scene) === String(showInScene));
    });

    this._dots.forEach((dot, i) => dot.classList.toggle('active', i === scene));
    this.finalRotation = scene >= 5;
  }

  // ───────────────────────── RENDER LOOP ─────────────────────────

  _animate() {
    this.animFrame = requestAnimationFrame(() => this._animate());
    const delta = this.clock.getDelta();
    const elapsed = this.clock.elapsedTime;

    // Gentle slow parallax drift on the 3D bg plane
    if (this.bgPlane) {
      this.bgPlane.rotation.z = Math.sin(elapsed * 0.06) * 0.03;
    }
    if (this.bgGlow) {
      // Breathe the glow intensity
      this.bgGlow.material.opacity = 0.75 + Math.sin(elapsed * 0.4) * 0.15;
    }

    // Auto-rotate in final scene; gentle idle sway otherwise
    this.vesselGroup.rotation.y += delta * (this.finalRotation ? 0.5 : 0.05);

    this.renderer.render(this.scene, this.camera);
  }
}

customElements.define('eco-product-story', EcoProductStory);